import { Card, Divider, Flex, SimpleGrid, Text, Title } from "@mantine/core";

import React from "react";
import { useTranslation } from "react-i18next";

export interface DyslexiaMetricSummary {
  totalFixations: number;
  averageFixationDuration: number;
  excessiveFixationsCount: number;
  excessiveFixationsPercentage: number;
  averageExcessiveFixationDuration: number;
  normalFixationsCount: number;
  normalFixationsPercentage: number;
  averageNormalFixationDuration: number;
  wordsPerMinute?: number | null;
}

export interface DyslexiaMetricsProps {
  metrics?: DyslexiaMetricSummary;
  columns?: { base: number; sm?: number };
}

const DEFAULT_METRICS: DyslexiaMetricSummary = {
  totalFixations: 0,
  averageFixationDuration: 0,
  excessiveFixationsCount: 0,
  excessiveFixationsPercentage: 0,
  averageExcessiveFixationDuration: 0,
  normalFixationsCount: 0,
  normalFixationsPercentage: 0,
  averageNormalFixationDuration: 0,
};

const formatSeconds = (value: number): string => `${value.toFixed(3)}s`;
const formatPercentage = (value: number): string => `${value.toFixed(2)}%`;
const formatWordsPerMinute = (value: number | null | undefined): string =>
  Number.isFinite(value) ? String(Math.round(value)) : "N/A";

const DyslexiaMetrics: React.FC<DyslexiaMetricsProps> = ({ metrics, columns }) => {
  const metricsData = metrics ?? DEFAULT_METRICS;
  const { t } = useTranslation();
  const gridColumns = columns ?? { base: 1, sm: 2 };

  const items = [
    { label: t("DY.totalFixations"), value: String(metricsData.totalFixations) },
    {
      label: t("DY.avgFixationDuration"),
      value: formatSeconds(metricsData.averageFixationDuration),
    },
    {
      label: t("DY.numberOfExcessiveFixations"),
      value: String(metricsData.excessiveFixationsCount),
    },
    {
      label: t("DY.excessiveFixationsPercentage"),
      value: formatPercentage(metricsData.excessiveFixationsPercentage),
    },
    {
      label: t("DY.avgExcessiveFixationDuration"),
      value: formatSeconds(metricsData.averageExcessiveFixationDuration),
    },
    { label: t("DY.numberOfNormalFixations"), value: String(metricsData.normalFixationsCount) },
    {
      label: t("DY.normalFixationsPercentage"),
      value: formatPercentage(metricsData.normalFixationsPercentage),
    },
    {
      label: t("DY.avgNormalFixationDuration"),
      value: formatSeconds(metricsData.averageNormalFixationDuration),
    },
    ...(metricsData.wordsPerMinute !== undefined
      ? [
          {
            label: t("DY.wordsPerMinute", { defaultValue: "Words per minute" }),
            value: formatWordsPerMinute(metricsData.wordsPerMinute),
          },
        ]
      : []),
  ];

  return (
    <Card withBorder radius="md" shadow="md" p="md" h="100%">
      <Title order={4}>Metrics</Title>
      <Divider size={"sm"} mb={"xs"} />
      <SimpleGrid cols={gridColumns} spacing="xs" verticalSpacing="xs">
        {items.map((item) => (
          <Flex key={item.label} justify={"space-between"} align={"center"} gap="sm">
            <Text fz={"sm"} fw={600} style={{ flex: 1 }}>
              {item.label}
            </Text>
            <Text>{item.value}</Text>
          </Flex>
        ))}
      </SimpleGrid>
    </Card>
  );
};

export default DyslexiaMetrics;
